import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, Users, UserCircle2 } from "lucide-react";

export default function Login() {
    const { user, login, register, childLogin, error, setError } = useAuth();
    const [tab, setTab] = useState("parent");
    const [mode, setMode] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [username, setUsername] = useState("");
    const [pin, setPin] = useState("");
    const [busy, setBusy] = useState(false);

    if (user) return <Navigate to="/" replace />;

    const submitParent = async (e) => {
        e.preventDefault();
        setBusy(true);
        if (mode === "login") await login(email, password);
        else await register(email, password, name);
        setBusy(false);
    };

    const submitChild = async (e) => {
        e.preventDefault();
        setBusy(true);
        await childLogin(username, pin);
        setBusy(false);
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF8F5] grain">
            <div className="relative hidden lg:flex items-center justify-center p-12 overflow-hidden">
                <div className="absolute inset-0 bg-[#1E293B]"></div>
                <img
                    src="https://images.unsplash.com/photo-1756723903313-b3d2f705f472?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover opacity-55"
                />
                <div className="relative z-10 max-w-md text-white">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs uppercase tracking-widest backdrop-blur">
                        <Sparkles className="h-3.5 w-3.5" /> Rotina em família
                    </div>
                    <h1 className="mt-6 text-5xl font-bold leading-[1.05]" style={{ fontFamily: "Outfit" }}>
                        Tarefas, banho e <span className="text-[#E5A93C]">mesada</span> em um lugar só.
                    </h1>
                    <p className="mt-6 text-white/85 text-lg">
                        Entre como pai/mãe para montar a rotina, ou como criança para fazer check-in das tarefas com foto.
                    </p>
                </div>
            </div>

            <div className="flex items-center justify-center p-6 sm:p-12">
                <Card className="w-full max-w-md border-[#E2DCD5] bg-white shadow-sm p-6 sm:p-8 rise">
                    <div className="flex items-center gap-2.5 mb-5">
                        <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#D96B43] text-white">
                            <Sparkles className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>RotinaKids</div>
                            <div className="text-[11px] uppercase tracking-widest text-[#8a7968]">gestão familiar</div>
                        </div>
                    </div>

                    <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }}>
                        <TabsList className="grid grid-cols-2 w-full rounded-full bg-[#F3EFEA] p-1">
                            <TabsTrigger value="parent" data-testid="tab-parent" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm">
                                <Users className="h-4 w-4 mr-1.5" /> Pai/Mãe
                            </TabsTrigger>
                            <TabsTrigger value="child" data-testid="tab-child" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm">
                                <UserCircle2 className="h-4 w-4 mr-1.5" /> Criança
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="parent" className="mt-5">
                            <h2 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>
                                {mode === "login" ? "Bem-vindo de volta" : "Criar conta"}
                            </h2>
                            <p className="text-sm text-slate-500 mt-1">
                                {mode === "login" ? "Entre para gerenciar as tarefas." : "Comece grátis em menos de 1 minuto."}
                            </p>
                            <form onSubmit={submitParent} className="mt-5 space-y-4">
                                {mode === "register" && (
                                    <div>
                                        <Label htmlFor="name">Seu nome</Label>
                                        <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" placeholder="Ex: Mariana" />
                                    </div>
                                )}
                                <div>
                                    <Label htmlFor="email">Email</Label>
                                    <Input id="email" type="email" data-testid={mode === "login" ? "login-email-input" : "register-email-input"} value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" placeholder="pai@familia.com" />
                                </div>
                                <div>
                                    <Label htmlFor="password">Senha</Label>
                                    <Input id="password" type="password" data-testid={mode === "login" ? "login-password-input" : "register-password-input"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5" placeholder="••••••" />
                                </div>
                                {error && tab === "parent" && (
                                    <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="auth-error">{error}</div>
                                )}
                                <Button type="submit" disabled={busy} data-testid={mode === "login" ? "login-submit-button" : "register-submit-button"} className="w-full rounded-full bg-[#D96B43] hover:bg-[#C25933] text-white h-11 font-semibold">
                                    {busy ? "Aguarde..." : mode === "login" ? "Entrar" : "Criar conta"}
                                </Button>
                            </form>
                            <div className="mt-5 text-center text-sm text-slate-500">
                                {mode === "login" ? (
                                    <>Primeira vez? <button onClick={() => setMode("register")} data-testid="switch-to-register" className="font-semibold text-[#D96B43] hover:underline">Criar conta</button></>
                                ) : (
                                    <>Já tem conta? <button onClick={() => setMode("login")} data-testid="switch-to-login" className="font-semibold text-[#D96B43] hover:underline">Entrar</button></>
                                )}
                            </div>
                        </TabsContent>

                        <TabsContent value="child" className="mt-5">
                            <h2 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Entrar como criança</h2>
                            <p className="text-sm text-slate-500 mt-1">Use seu apelido e seu PIN.</p>
                            <form onSubmit={submitChild} className="mt-5 space-y-4">
                                <div>
                                    <Label htmlFor="username">Apelido</Label>
                                    <Input id="username" data-testid="child-login-username" value={username} onChange={(e) => setUsername(e.target.value.replace(/\s/g, "").toLowerCase())} required className="mt-1.5" placeholder="ex: lucas" />
                                </div>
                                <div>
                                    <Label htmlFor="pin">PIN</Label>
                                    <Input id="pin" data-testid="child-login-pin" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} required inputMode="numeric" type="password" className="mt-1.5 text-center tracking-[0.5em] text-lg" placeholder="••••" />
                                </div>
                                {error && tab === "child" && (
                                    <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="child-auth-error">{error}</div>
                                )}
                                <Button type="submit" disabled={busy} data-testid="child-login-submit" className="w-full rounded-full bg-[#4A7C59] hover:bg-[#3a6549] text-white h-11 font-semibold">
                                    {busy ? "Aguarde..." : "Entrar"}
                                </Button>
                            </form>
                            <div className="mt-4 rounded-lg bg-[#F3EFEA] p-3 text-xs text-slate-500">
                                Peça pro seu pai/mãe cadastrar seu apelido e PIN.
                            </div>
                        </TabsContent>
                    </Tabs>
                </Card>
            </div>
        </div>
    );
}
